import { useId } from 'react';
import { T, useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { Switch } from 'src/components/ui/switch';
import { CronInput } from 'src/components/common/CronInput';
import { EditorDialogShell } from '../../settings/organization/components/EditorDialogShell';
import {
  isAgentScheduleDraftDirty,
  isAgentScheduleDraftValid,
  type AgentScheduleDraft,
} from '../agentScheduleDraft';

/**
 * Common IANA zones offered in the picker. The current draft's zone is always
 * added if it is not one of these, so a schedule saved elsewhere still shows
 * its real zone instead of a blank select.
 */
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
];

function useTimezoneOptions(current: string) {
  const gt = useGT();
  const labels: Record<string, string> = {
    UTC: gt('UTC', { context: 'timezone select option' }),
    'America/New_York': gt('US Eastern (New York)', {
      context: 'timezone select option',
    }),
    'America/Chicago': gt('US Central (Chicago)', {
      context: 'timezone select option',
    }),
    'America/Denver': gt('US Mountain (Denver)', {
      context: 'timezone select option',
    }),
    'America/Los_Angeles': gt('US Pacific (Los Angeles)', {
      context: 'timezone select option',
    }),
    'Europe/London': gt('UK (London)', {
      context: 'timezone select option',
    }),
    'Europe/Paris': gt('Central Europe (Paris)', {
      context: 'timezone select option',
    }),
    'Asia/Tokyo': gt('Japan (Tokyo)', {
      context: 'timezone select option',
    }),
    'Asia/Singapore': gt('Singapore', {
      context: 'timezone select option',
    }),
    'Australia/Sydney': gt('Australia Eastern (Sydney)', {
      context: 'timezone select option',
    }),
  };
  const zones = COMMON_TIMEZONES.includes(current)
    ? COMMON_TIMEZONES
    : [current, ...COMMON_TIMEZONES];
  return zones.map((value) => ({ value, label: labels[value] ?? value }));
}

/** The dialog's form body — only mounted while the dialog is open. */
function AgentScheduleFields({
  draft,
  onChange,
}: {
  draft: AgentScheduleDraft;
  onChange: (next: AgentScheduleDraft) => void;
}) {
  const gt = useGT();
  const idPrefix = useId();
  const timezoneOptions = useTimezoneOptions(draft.timezone);
  const timezoneLabel =
    timezoneOptions.find((option) => option.value === draft.timezone)?.label ??
    draft.timezone;

  return (
    <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto px-6 py-4">
      <CronInput
        value={draft.cronExpression}
        onChange={(cronExpression) => onChange({ ...draft, cronExpression })}
        label={gt('When to run ({timezone})', {
          timezone: timezoneLabel,
          context:
            'label above the cron picker in the agent schedule dialog; timezone is the friendly zone name',
        })}
        defaultFrequency="daily"
        showAdvanced
      />
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-timezone`}>
          <T context="agent schedule form field label">Timezone</T>
        </Label>
        <Select
          value={draft.timezone}
          onValueChange={(timezone) => onChange({ ...draft, timezone })}
        >
          <SelectTrigger id={`${idPrefix}-timezone`}>
            <SelectValue
              placeholder={gt('Select timezone', {
                context: 'agent schedule timezone placeholder',
              })}
            />
          </SelectTrigger>
          <SelectContent>
            {timezoneOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-muted-foreground">
          <T context="hint under the agent schedule timezone field">
            The schedule's times are read in this timezone.
          </T>
        </p>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${idPrefix}-description`}>
          <T context="agent schedule form field label">Description</T>
        </Label>
        <Input
          id={`${idPrefix}-description`}
          value={draft.description}
          maxLength={500}
          onChange={(e) => onChange({ ...draft, description: e.target.value })}
          placeholder={gt('Morning sweep of new invoices', {
            context: 'example agent schedule description placeholder',
          })}
        />
      </div>
      <div className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={`${idPrefix}-enabled`}>
            <T context="agent schedule form field label">Enabled</T>
          </Label>
          <p className="text-[11px] text-muted-foreground">
            <T context="hint under the agent schedule enabled toggle">
              Disabled schedules are kept but never start a run.
            </T>
          </p>
        </div>
        <Switch
          id={`${idPrefix}-enabled`}
          checked={draft.isEnabled}
          onCheckedChange={(isEnabled) => onChange({ ...draft, isEnabled })}
        />
      </div>
    </div>
  );
}

/**
 * Create/edit dialog for one agent schedule: the cron, its timezone, an
 * optional note, and whether it is live. Mirrors the agent page editor.
 */
export function AgentScheduleDialog({
  draft,
  initialDraft,
  onChange,
  onClose,
  onSave,
  isSaving,
}: {
  draft: AgentScheduleDraft | null;
  /** The draft as opened, for the unsaved-changes warning. */
  initialDraft: AgentScheduleDraft | null;
  onChange: (next: AgentScheduleDraft) => void;
  onClose: () => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const gt = useGT();
  const dirty =
    draft !== null &&
    initialDraft !== null &&
    isAgentScheduleDraftDirty(draft, initialDraft);
  const canSave = draft !== null && isAgentScheduleDraftValid(draft);
  const isEditing = draft?.id != null;

  return (
    <EditorDialogShell
      open={draft !== null}
      dirty={dirty}
      onClose={onClose}
      title={
        isEditing ? (
          <T context="dialog title, edit an agent run schedule">
            Edit schedule
          </T>
        ) : (
          <T context="dialog title, create an agent run schedule">
            New schedule
          </T>
        )
      }
      discard={{
        title: gt('Discard unsaved changes?', {
          context: 'unsaved-changes leave warning dialog title',
        }),
        message: gt(
          'Discard unsaved changes to this schedule? Your edits will be lost.',
          {
            context:
              'confirm dialog before closing the agent schedule editor with unsaved changes',
          },
        ),
        confirmText: gt('Leave without saving', {
          context: 'unsaved-changes leave warning, confirm button',
        }),
        cancelText: gt('Keep editing', {
          context: 'unsaved-changes leave warning, cancel button',
        }),
      }}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose} disabled={isSaving}>
            <T context="button, close the agent schedule editor">Cancel</T>
          </Button>
          <Button disabled={!canSave || isSaving} onClick={onSave}>
            {isSaving ? (
              <T context="button label while saving an agent schedule">
                Saving…
              </T>
            ) : isEditing ? (
              <T context="button, save the agent schedule changes">Save</T>
            ) : (
              <T context="button, create the agent schedule">Create</T>
            )}
          </Button>
        </>
      )}
    >
      {draft && <AgentScheduleFields draft={draft} onChange={onChange} />}
    </EditorDialogShell>
  );
}
